// src/firebase/auth.js
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  sendPasswordResetEmail,
  updateProfile,
  // sendEmailVerification eliminado - solo sistema personalizado
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { auth, db, functions } from './config';
import { DEFAULT_IMAGES, DEFAULT_COLORS } from '../constants/defaults';
import { createVerificationLink } from '../utils/urlUtils';

// Proveedor de Google
const googleProvider = new GoogleAuthProvider();

// Registrar un nuevo usuario con email y contraseña
export const registerWithEmail = async (email, password, username) => {
  try {
    // Validar que el nombre de usuario no contenga la palabra "admin"
    if (username.toLowerCase().includes('admin')) {
      throw new Error('El nombre de usuario no puede contener la palabra "admin"');
    }
    
    // Crear usuario en Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Actualizar el perfil con el nombre de usuario y la foto predeterminada
    await updateProfile(user, {
      displayName: username,
      photoURL: DEFAULT_IMAGES.PROFILE // Establecer imagen de perfil en Auth también
    });
    
    // Generar token de verificación personalizado y enviar email con nuestra plantilla
    console.log('📧 [registerWithEmail] === ENVIANDO EMAIL DE VERIFICACIÓN ===');
    
    try {
      console.log('📧 [registerWithEmail] Usando sistema personalizado exclusivamente...');
      
      // Generar link de verificación personalizado
      const verificationToken = btoa(`${user.uid}_${Date.now()}_${Math.random()}`);
      const verificationLink = createVerificationLink(verificationToken, user.uid);
      
      const sendCustomVerificationEmail = httpsCallable(functions, 'sendEmailVerification');
      
      await sendCustomVerificationEmail({
        email: user.email,
        userName: username,
        verificationLink: verificationLink
      });
      
      console.log('📧 [registerWithEmail] ✅ Resend exitoso - Email personalizado enviado');
      
      // Guardar token en localStorage para verificación posterior
      localStorage.setItem('verificationToken', verificationToken);
      localStorage.setItem('verificationEmail', user.email);
      
    } catch (resendError) {
      console.error('❌ [registerWithEmail] Error enviando email personalizado:', resendError);
      throw new Error('No se pudo enviar el email de verificación. Por favor intenta nuevamente en unos minutos.');
    }
    
    // Verificar que las URL de las imágenes predeterminadas estén disponibles
    console.log("Imágenes predeterminadas:", DEFAULT_IMAGES);
    
    // Crear documento de usuario en Firestore
    const userData = {
      uid: user.uid,
      email: user.email,
      email_lower: email.toLowerCase(), // Para búsquedas case-insensitive
      username: username,
      username_lower: username.toLowerCase(), // Para búsquedas case-insensitive
      displayName: username,
      profileImage: DEFAULT_IMAGES.PROFILE, // Imagen de perfil predeterminada
      banner: DEFAULT_IMAGES.BANNER, // Banner predeterminado
      backgroundImage: DEFAULT_IMAGES.BACKGROUND, // Fondo predeterminado
      primaryColor: DEFAULT_COLORS.PRIMARY, // Color primario predeterminado
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      role: 'user', // roles: user, premium, admin
      verified: false, // Estado de verificación de email
      bio: '',
      location: '',
      profession: '',
      portfolioItems: [],
      followers: 0,
      following: 0,
      blocked: [],
      settings: {
        emailNotifications: true,
        darkMode: false
      }
    };
    
    await setDoc(doc(db, 'users', user.uid), userData);
    
    return { user };
  } catch (error) {
    console.error('Error al registrar usuario:', error);
    throw error;
  }
};

// Iniciar sesión con email y contraseña
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user };
  } catch (error) {
    console.error('Error al iniciar sesión:', error);
    throw error;
  }
};

// Iniciar sesión con Google
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // Verificar si el usuario ya existe en Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    // Si no existe, crear un nuevo documento
    if (!userDoc.exists()) {
      // Crear un nombre de usuario basado en displayName o email
      let username = user.displayName || user.email.split('@')[0];
      
      // Validar que el nombre de usuario no contenga la palabra "admin"
      if (username.toLowerCase().includes('admin')) {
        // Generar un nombre de usuario alternativo sin la palabra "admin"
        username = username.toLowerCase().replace('admin', 'usuario') + Math.floor(Math.random() * 1000);
      }
      
      // Verificar que las URL de las imágenes predeterminadas estén disponibles
      console.log("Imágenes predeterminadas (Google Sign In):", DEFAULT_IMAGES);
      
      // Si no hay foto de perfil de Google, actualizar el perfil con la imagen predeterminada
      if (!user.photoURL) {
        await updateProfile(user, {
          photoURL: DEFAULT_IMAGES.PROFILE
        });
      }
      
      const userData = {
        uid: user.uid,
        email: user.email,
        email_lower: user.email.toLowerCase(),
        username: username,
        username_lower: username.toLowerCase(),
        displayName: username,
        profileImage: user.photoURL || DEFAULT_IMAGES.PROFILE, // Usar foto de Google o predeterminada
        banner: DEFAULT_IMAGES.BANNER, // Banner predeterminado
        backgroundImage: DEFAULT_IMAGES.BACKGROUND, // Fondo predeterminado
        primaryColor: DEFAULT_COLORS.PRIMARY, // Color primario predeterminado
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        role: 'user',
        verified: user.emailVerified,
        bio: '',
        location: '',
        profession: '',
        portfolioItems: [],
        followers: 0,
        following: 0,
        blocked: [],
        settings: {
          emailNotifications: true,
          darkMode: false
        }
      };


      await setDoc(doc(db, 'users', user.uid), userData);
    }
    
    return { user };
  } catch (error) {
    console.error('Error al iniciar sesión con Google:', error);
    throw error;
  }
};

// Enviar email para restablecer contraseña
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error('Error al enviar email de restablecimiento:', error);
    throw error;
  }
};